/**
* @license Apache-2.0
*
* Copyright (c) 2026 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import toUniqueNormalizedIndices from '@stdlib/ndarray-base-to-unique-normalized-indices';
import getDType from '@stdlib/ndarray-base-dtype';
import getShape from '@stdlib/ndarray-base-shape';
import getStrides from '@stdlib/ndarray-base-strides';
import getOffset from '@stdlib/ndarray-base-offset';
import getOrder from '@stdlib/ndarray-base-order';
import getData from '@stdlib/ndarray-base-data-buffer';
import join from '@stdlib/array-base-join';
import min from '@stdlib/math-base-special-min';
import format from '@stdlib/error-tools-fmtprodmsg';


// MAIN //

/**
* Returns a view of the diagonal of a matrix (or stack of matrices).
*
* ## Notes
*
* -   The order of the dimension indices contained in `dims` matters. The first element specifies the row-like dimension. The second element specifies the column-like dimension.
* -   Each provided dimension index must reside on the interval `[-ndims, ndims-1]`.
* -   The diagonal offset `k` is interpreted as `column - row`. Accordingly, when `k = 0`, the function returns the main diagonal; when `k > 0`, the function returns the diagonal above the main diagonal; and when `k < 0`, the function returns the diagonal below the main diagonal.
* -   The returned ndarray is a **view** of the input ndarray. Accordingly, writing to the original ndarray will **mutate** the returned ndarray and vice versa.
* -   The `writable` parameter **only** applies to ndarray constructors supporting **read-only** instances.
*
* @param {ndarray} x - input array
* @param {IntegerArray} dims - dimension indices defining the plane from which to extract the diagonal
* @param {integer} k - diagonal offset
* @param {boolean} writable - boolean indicating whether the returned ndarray should be writable
* @throws {RangeError} must provide exactly two dimension indices
* @throws {RangeError} must provide an ndarray having two or more dimensions
* @throws {RangeError} must provide valid dimension indices
* @throws {Error} must provide unique dimension indices
* @returns {ndarray} ndarray view
*
* @example
* import array from '@stdlib/ndarray-array';
*
* var x = array( [ [ 1.0, 2.0, 3.0 ], [ 4.0, 5.0, 6.0 ], [ 7.0, 8.0, 9.0 ] ] );
* // returns <ndarray>[ [ 1.0, 2.0, 3.0 ], [ 4.0, 5.0, 6.0 ], [ 7.0, 8.0, 9.0 ] ]
*
* var y = diagonal( x, [ 0, 1 ], 0, false );
* // returns <ndarray>[ 1.0, 5.0, 9.0 ]
*/
function diagonal( x, dims, k, writable ) {
	var strides;
	var offset;
	var ndims;
	var shape;
	var rows;
	var cols;
	var sh;
	var st;
	var sr;
	var sc;
	var ro;
	var co;
	var d;
	var L;
	var i;

	if ( dims.length !== 2 ) {
		throw new RangeError( format( 'invalid argument. Must provide exactly two dimension indices. Value: `[%s]`.', join( dims, ', ' ) ) );
	}
	sh = getShape( x );
	ndims = sh.length;
	if ( ndims < 2 ) {
		throw new RangeError( format( 'invalid argument. First argument must be an ndarray having two or more dimensions. Number of dimensions: %d.', ndims ) );
	}
	st = getStrides( x );

	// Resolve dimension indices...
	d = toUniqueNormalizedIndices( dims, ndims-1 );
	if ( d === null ) {
		throw new RangeError( format( 'invalid argument. Specified dimension index is out-of-bounds. Must be on the interval: [-%u, %u]. Value: `[%s]`.', ndims, ndims-1, join( dims, ', ' ) ) );
	}
	if ( d.length !== dims.length ) {
		throw new Error( format( 'invalid argument. Must provide unique dimension indices. Value: `[%s]`.', join( dims, ', ' ) ) );
	}
	sr = st[ d[0] ];
	sc = st[ d[1] ];

	// Resolve the row and column offsets corresponding to the diagonal offset `k = column - row`:
	co = ( k > 0 ) ? k : 0;
	ro = co - k;

	// Compute the length of the diagonal:
	rows = sh[ d[0] ] - ro;
	cols = sh[ d[1] ] - co;
	L = min( rows, cols );
	if ( L < 0 ) {
		L = 0;
	}

	// Adjust the offset so that we point to the first element along the specified diagonal:
	offset = getOffset( x );
	if ( L > 0 ) {
		offset += ( co*sc ) + ( ro*sr );
	}

	// Drop the specified dimensions and append the diagonal dimension:
	shape = [];
	strides = [];
	for ( i = 0; i < ndims; i++ ) {
		if ( i === d[0] || i === d[1] ) {
			continue;
		}
		shape.push( sh[ i ] );
		strides.push( st[ i ] );
	}
	shape.push( L );
	strides.push( sr + sc );

	return new x.constructor( getDType( x ), getData( x ), shape, strides, offset, getOrder( x ), { // eslint-disable-line max-len
		'readonly': !writable
	});
}


// EXPORTS //

export default diagonal;
